import { Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@material-ui/core";
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { FC, useEffect, useState } from "react";
import { useDataProvider } from "ra-core";
import { useNotify } from "react-admin";
import FileSaver from "file-saver";
import Loader from '../common/loader';
import { ReportItem } from "../../types/report";
import "./style.css";
import PaginationTable from "../common/paginationTable";
import { getDate } from "../../helpers/dateHelper";
import HeadTable from "../common/sortTable/sortTable";
import { getFieldByTitle } from "../../helpers/sortByTitle";
import "./style.css";

const TEN_SECONDS_MS = 10000;

const convertToBlob = (data, type, fileName) => {
  let blob = new Blob([data], { type });
  fileName =
    blob.type == "application/zip" ? `${fileName}.zip` : `${fileName}.csv`;
  FileSaver.saveAs(blob, fileName);
};

interface State {
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
}

const ReportsList: FC = () => {
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [reports, setReports] = useState<Array<ReportItem>>([]);
  const [state, setState] = useState<State>({
    total: 0,
    page: 1,
    perPage: 10,
    field: 'createdAt',
    order: 'desc',
  })
  const [isDownloading, setIsDownloading] = useState(false)

  const getReportList = async (page: number, perPage: number, field: string, order: string,) => {
    const result = await dataProvider.getReports({
      filter: {},
      pagination: { page, perPage },
      sort: { field: field, order: order },
    });
    setReports(result.data.reports);
    setState({
      page,
      perPage,
      field,
      order,
      total: result.data.allCount
    })
  };

  const download = async (id: string, name: string) => {
    if (!isDownloading) {
      setIsDownloading(true)
      const result = await dataProvider.downloadReport(id);
      convertToBlob(result.data.data, result.data.headers["content-type"], name);
      const timeout = setTimeout(() => {
        setIsDownloading(false)
      }, 3000);
    }
  };

  useEffect(() => {
    getReportList(state.page, state.perPage, state.field, state.order);
  }, []);

  useEffect(() => {
    // Poll only if there are reports still generating
    const interval = setInterval(() => {
      const inProgress = reports.some(r => (r as any).status === 'IN_PROGRESS')
      if (inProgress) {
        getReportList(state.page, state.perPage, state.field, state.order);
      }
    }, TEN_SECONDS_MS);

    return () => clearInterval(interval);
  }, [reports]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    getReportList(newPage + 1, state.perPage, state.field, state.order)
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage)
    getReportList(page, state.perPage, state.field, state.order)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const perPage = parseInt(event.target.value, 10)
    getReportList(state.page, perPage, state.field, state.order)
  }

  const handleFirstPage = () => {
    getReportList(1, state.perPage, state.field, state.order)
  }

  const handleSort = (direction: string, field: string) => {
    field = getFieldByTitle(field)
    getReportList(state.page, state.perPage, field, direction)
  }

  return (
    <Box>
      <Card>
        <Box padding="2em">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell id="active-table-head">
                    <HeadTable title="Name" sort={handleSort} />
                  </TableCell>
                  <TableCell id="active-table-head">
                    <HeadTable title="Created Date" sort={handleSort} />
                  </TableCell>

                  <TableCell id="active-table-head-actions">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              {reports.map((item, i) => {
                return (
                  <TableBody key={i} >
                    <TableRow className="test">
                      <TableCell>
                        {item.name}
                      </TableCell>
                      <TableCell>
                        {getDate(new Date(item.createdAt))}
                      </TableCell>
                      <TableCell>
                        {item.status === 'IN_PROGRESS' && 
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <Loader small  />
                          </Box>
                        }
                        {item.status === 'ERROR' && 
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <span style={{ color: 'red' }}>Error</span>
                          </Box>
                        }
                        {item.storeKey && 
                          <Box display="flex" justifyContent="center" alignItems="center" className={`${isDownloading ? 'disable-download' : 'download-icon'}`} onClick={() => download(item._id, item.name)}>
                            <GetAppIcon />
                          </Box>
                        }
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )
              })}
            </Table>
          </TableContainer>
          <PaginationTable
            page={state.page}
            perPage={state.perPage}
            collectionLength={reports.length}
            total={state.total}
            handleChangePage={handleChangePage}
            handleLastPage={handleLastPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleFirstPage={handleFirstPage}
            title="Reports"
          />

        </Box>
      </Card>
    </Box>
  );
};

export default ReportsList;
